import React, { useRef } from 'react';
import { useVRBuild } from './build/vrBuildState';
import { locomotionLocked } from './locomotionLock';
import { useFrame } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import * as THREE from 'three';
import { Object3D } from 'three';

interface VRLocomotionProps {
  target: React.RefObject<Object3D | null>;
  moveSpeed?: number;
  turnSpeed?: number;
  enableTeleport?: boolean;
  enableSmoothLocomotion?: boolean;
  debugThumbstick?: boolean;
}

const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

const MOVE_DEADZONE = 0.15;
const SNAP_DEADZONE = 0.5;
const SNAP_DEGREES = 30;

/**
 * VRLocomotion — reads raw session.inputSources each frame so we bypass the
 * @pmndrs/xr inputSourceStates abstraction that can drop controllers on Quest.
 *
 * Left thumbstick  → smooth move (forward/back/strafe relative to head direction)
 * Right thumbstick → snap turn (30° per click, left or right)
 */
const VRLocomotion: React.FC<VRLocomotionProps> = ({
  target,
  moveSpeed = 2.5,
  enableSmoothLocomotion = true,
}) => {
  const isPresenting = useXR((s) => !!s.session);
  const sessionRef = useRef<XRSession | null>(null);
  const canSnapRef = useRef(true);

  // Keep a live ref to the XR session without re-rendering
  useXR((s) => {
    sessionRef.current = s.session ?? null;
    return s.session;
  });

  useFrame((state, delta) => {
    if (!isPresenting || !enableSmoothLocomotion || !target.current) return;
    const session = sessionRef.current;
    if (!session) return;

    const rig = target.current;
    // An arcade game (or anything else) owns the sticks: no move, no turn.
    // The snap re-arms only once the stick is back in the centre afterwards.
    if (locomotionLocked()) { canSnapRef.current = false; return; }

    for (const source of session.inputSources) {
      const gp = source.gamepad;
      if (!gp || !gp.axes || gp.axes.length < 2) continue;

      // Quest controllers report thumbstick on axes[2]/[3].
      // Fall back to axes[0]/[1] if only 2 axes are present.
      const offset = gp.axes.length >= 4 ? 2 : 0;
      const axisX = gp.axes[offset] ?? 0;
      const axisY = gp.axes[offset + 1] ?? 0;

      if (source.handedness === 'left') {
        // ── Smooth movement ──────────────────────────────────────────────
        const mx = Math.abs(axisX) > MOVE_DEADZONE ? axisX : 0;
        const my = Math.abs(axisY) > MOVE_DEADZONE ? axisY : 0;

        if (mx !== 0 || my !== 0) {
          // Project camera forward onto horizontal plane
          state.camera.getWorldDirection(_forward);
          _forward.y = 0;
          if (_forward.lengthSq() < 0.0001) _forward.set(0, 0, -1);
          _forward.normalize();
          _right.crossVectors(_forward, _up).normalize();

          // axisY negative = stick forward = move forward
          rig.position.addScaledVector(_forward, -my * moveSpeed * delta);
          rig.position.addScaledVector(_right, mx * moveSpeed * delta);
        }

      } else if (source.handedness === 'right') {
        // ── Snap turn ────────────────────────────────────────────────────
        // While you hold something in build mode the right stick pushes /
        // pulls / spins it instead (VRGrab).
        if (useVRBuild.getState().handling) { canSnapRef.current = false; continue; }
        const tx = axisX;

        if (Math.abs(tx) < SNAP_DEADZONE) {
          // Stick returned to center — allow next snap
          canSnapRef.current = true;
        } else if (canSnapRef.current) {
          canSnapRef.current = false;

          const snapRad = (tx > 0 ? -1 : 1) * (SNAP_DEGREES * Math.PI / 180);

          // Rotate rig around the camera's current XZ position so the
          // user doesn't drift after snapping.
          const camWorld = new THREE.Vector3();
          state.camera.getWorldPosition(camWorld);

          // Offset from rig to camera (horizontal only)
          const dx = camWorld.x - rig.position.x;
          const dz = camWorld.z - rig.position.z;

          // Rotate that offset around Y
          const cos = Math.cos(snapRad);
          const sin = Math.sin(snapRad);
          const rdx = cos * dx - sin * dz;
          const rdz = sin * dx + cos * dz;

          // Adjust rig so camera stays in same world position after rotation
          rig.position.x += dx - rdx;
          rig.position.z += dz - rdz;
          rig.rotation.y += snapRad;
        }
      }
    }
  });

  if (!isPresenting) return null;
  return null;
};

export default VRLocomotion;
